import React, { useState, useEffect } from 'react';
import { useDeviceMonitoring } from 'web/contexts/DeviceMonitoringContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spinner } from './Spinner';
import { useNavigate } from 'react-router-dom';
import { useCurrentOrganization } from 'web/hooks/useCurrentOrganization';
import { useGetDefaultPatient } from 'web/services/api/queries';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { BLEService } from 'web/services/BLEService';

// Type for device status tracking
interface DeviceStatus {
  isReconnecting?: boolean;
}

export const DeviceMonitoringBar: React.FC = () => {
  const {
    devices,
    isActiveRecording,
    isDeviceMonitored,
    isDeviceConnected,
    getConnectionStatusDetails,
    notification,
    stopMonitoring,
    startMonitoring,
    syncStatus,
    monitoredDeviceStatuses,
  } = useDeviceMonitoring();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMonitoringBarVisible, setIsMonitoringBarVisible] = useState(false);
  const [deviceStatuses, setDeviceStatuses] = useState<Record<string, DeviceStatus>>({});
  const navigate = useNavigate();
  const organizationId = useCurrentOrganization()?.currentOrganization?.id?.toString() || '';
  const { data: defaultPatient } = useGetDefaultPatient();
  
  // Show the monitoring bar if any devices are being monitored
  useEffect(() => {
    // Get monitored devices count from monitoredDeviceStatuses
    const monitoredDevicesFromStatus = Object.keys(monitoredDeviceStatuses).length > 0;
    
    // Also check the traditional way
    const monitoredDevicesCount = devices.filter(device => isDeviceMonitored(device.id)).length;
    
    // Show if either condition is true
    setIsMonitoringBarVisible(monitoredDevicesCount > 0 || monitoredDevicesFromStatus);
  }, [devices, isDeviceMonitored, monitoredDeviceStatuses]);

  if (!isMonitoringBarVisible) return null;

  const monitoredDevices = devices.filter(device => isDeviceMonitored(device.id));
  
  const goToDevicesPage = () => {
    navigate('/nxvet-devices');
  };

  return (
    <div className="fixed bottom-16 right-4 z-50 flex flex-col items-end">
      {notification && (
        <div 
          className={`mb-4 p-3 rounded-lg shadow-lg max-w-sm text-white 
            ${notification.type === 'error' ? 'bg-red-500' : 
              notification.type === 'warning' ? 'bg-amber-500' : 
              notification.type === 'success' ? 'bg-green-500' : 'bg-blue-500'}`}
        >
          {notification.message}
        </div>
      )}
      
      <div 
        className={`bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-300 overflow-hidden
          ${isExpanded ? 'w-80' : 'w-auto'}`}
      >
        <div 
          className="bg-gray-100 p-2 cursor-pointer flex items-center justify-between"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center">
            <FontAwesomeIcon icon="microchip" className={`mr-2 ${monitoredDevices.some(d => syncStatus?.[d.id]?.isAutoSyncing) ? 'text-blue-600 animate-pulse' : 'text-blue-600'}`} />
            <span className="font-medium text-gray-800">
              <span className="flex items-center">
                Devices ({monitoredDevices.length})
                {monitoredDevices.some(d => syncStatus?.[d.id]?.isAutoSyncing) && (
                  <span className="ml-2 text-blue-600 font-medium animate-pulse flex items-center">
                    <span className="mr-1">Auto-Syncing</span>
                  </span>
                )}
              </span>
            </span>
          </div>
          <div className="flex items-center">
            {monitoredDevices.some(device => {
              const deviceSyncStatus = syncStatus?.[device.id];
              const isRecording = isActiveRecording[device.id];
              const isSyncing = deviceSyncStatus?.isAutoSyncing;
              
              // Return true if any device is recording or syncing
              return isRecording || isSyncing;
            }) && (
              <span className="mr-2 flex items-center gap-1">
                {(() => {
                  // Only show recording devices here, syncing is already in the title
                  const recordingDevices = monitoredDevices.filter(d => isActiveRecording[d.id]);
                  
                  if (recordingDevices.length > 0) {
                    const deviceName = recordingDevices.length === 1 
                      ? (recordingDevices[0].friendlyName || recordingDevices[0].name)
                      : `${recordingDevices.length} devices`;
                    return (
                      <>
                        <span className="inline-block h-2 w-2 bg-purple-600 rounded-full animate-pulse"></span>
                        <span className="text-xs text-purple-600 font-medium">
                          {`Recording ${recordingDevices.length === 1 ? deviceName : ''}`}
                        </span>
                      </>
                    );
                  }
                  return null;
                })()}
              </span>
            )}
            <FontAwesomeIcon 
              icon={isExpanded ? "chevron-down" : "chevron-up"} 
              className="ml-2 text-gray-500" 
            />
          </div>
        </div>
        
        {isExpanded && (
          <div className="max-h-96 overflow-y-auto p-2">
            {monitoredDevices.map(device => {
              const connectionDetails = getConnectionStatusDetails(device.id);
              const deviceStatus = deviceStatuses[device.id] || {};
              const deviceSyncStatus = syncStatus?.[device.id] || { isAutoSyncing: false, syncStage: 'idle' };
              let statusBgColor = 'bg-gray-400';
              let statusTextColor = 'text-gray-700';
              
              if (connectionDetails.status === 'connected') {
                statusBgColor = 'bg-green-500';
                statusTextColor = 'text-green-700';
              } else if (connectionDetails.status === 'connecting' || deviceStatus.isReconnecting) {
                statusBgColor = 'bg-yellow-500';
                statusTextColor = 'text-yellow-700';
              } else if (connectionDetails.status === 'disconnected') {
                statusBgColor = 'bg-red-500';
                statusTextColor = 'text-red-700';
              }
              
              return (
                <div key={device.id} className="mb-2 p-2 border border-gray-200 rounded bg-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium">
                        {device.friendlyName || device.name}
                      </div>
                      <div className="flex items-center mt-1">
                        <span className={`inline-block h-3 w-3 rounded-full ${statusBgColor} ${isDeviceConnected(device.id) ? 'animate-pulse' : ''} mr-2`}></span>
                        <span className={`text-xs ${statusTextColor}`}>
                          {deviceStatus.isReconnecting ? 'Reconnecting...' : connectionDetails.statusText}
                        </span>
                        {(connectionDetails.status === 'connecting' || deviceStatus.isReconnecting) && (
                          <Spinner className="h-3 w-3 ml-1" />
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      {isActiveRecording[device.id] && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium flex items-center">
                          <span className="inline-block h-2 w-2 bg-purple-600 rounded-full animate-pulse mr-1"></span>
                          Recording
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {deviceSyncStatus.isAutoSyncing && (
                    <div className="mt-2 bg-blue-50 p-3 rounded-md border border-blue-200 text-xs">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-blue-700 flex items-center">
                          <FontAwesomeIcon icon="sync" className="mr-1.5 animate-spin" />
                          {deviceSyncStatus.syncStage?.includes('Transferring file') ? 'Data Transfer' : 'Device Sync'}
                        </span>
                        {deviceSyncStatus.filesTransferred && deviceSyncStatus.filesTransferred > 0 && (
                          <span className="text-blue-600 font-medium px-2 py-0.5 bg-blue-100 rounded-full">
                            {deviceSyncStatus.filesTransferred} file{deviceSyncStatus.filesTransferred !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      
                      {/* Sync stage indicators */}
                      <div className="flex flex-col gap-1">
                        {(() => {
                          // Only show the transferring data stage
                          return (
                            <div 
                              className="flex items-center gap-2 py-2 px-2 rounded bg-blue-100 text-blue-800"
                            >
                              <FontAwesomeIcon 
                                icon={"exchange-alt" as IconProp}
                                className="animate-pulse"
                              />
                              <span className="font-medium">
                                Transferring data
                                {deviceSyncStatus.syncProgress !== undefined && (
                                  <span className="ml-1">({deviceSyncStatus.syncProgress.toFixed(0)}%)</span>
                                )}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      
                      {/* Progress bar - only show for file transfers */}
                      {deviceSyncStatus.syncProgress !== undefined && deviceSyncStatus.syncStage?.includes('Transferring file') && (
                        <div className="mt-2">
                          <div className="flex justify-between text-xs text-blue-700 mb-1">
                            <span>Data Transfer Progress</span>
                            <span className="font-medium">{deviceSyncStatus.syncProgress.toFixed(1)}%</span>
                          </div>
                          <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                            <div 
                              className="bg-blue-600 h-full rounded-full transition-all duration-300"
                              style={{ width: `${deviceSyncStatus.syncProgress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {!deviceSyncStatus.isAutoSyncing && deviceSyncStatus.syncStage && deviceSyncStatus.syncStage !== 'idle' && (
                    <div className="mt-2 bg-green-50 p-2 rounded text-xs text-green-700">
                      <span className="font-medium flex items-center">
                        <FontAwesomeIcon icon="check-circle" className="mr-1" />
                        {deviceSyncStatus.syncStage}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between mt-2">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const fullDevice = await BLEService.requestDevice(device.name);
                          await stopMonitoring(fullDevice);
                        } catch (error) {
                          console.error('Error stopping monitoring:', error);
                        }
                      }}
                      className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 transition"
                    >
                      Stop Monitoring
                    </button>
                    
                    {!isDeviceConnected(device.id) && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            // Show reconnecting status
                            setDeviceStatuses(prev => ({
                              ...prev,
                              [device.id]: { ...prev[device.id], isReconnecting: true }
                            }));
                            
                            const fullDevice = await BLEService.requestDevice(device.name);
                            
                            // Try to reconnect by restarting monitoring
                            await stopMonitoring(fullDevice);
                            
                            // Brief delay to ensure clean disconnect
                            await new Promise(resolve => setTimeout(resolve, 500));
                            
                            // Start monitoring again
                            await startMonitoring(fullDevice, organizationId, defaultPatient?.id);
                          } catch (error) {
                            console.error('Error reconnecting to device:', error);
                          } finally {
                            // Clear reconnecting status
                            setDeviceStatuses(prev => ({
                              ...prev,
                              [device.id]: { ...prev[device.id], isReconnecting: false }
                            }));
                          }
                        }}
                        className="text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 rounded text-white transition flex items-center gap-1"
                      >
                        <FontAwesomeIcon icon="sync" className="text-xs" />
                        Reconnect
                      </button>
                    )}
                    
                    <button
                      onClick={goToDevicesPage}
                      className="text-xs px-2 py-1 bg-blue-500 hover:bg-blue-600 rounded text-white transition"
                    >
                      View All
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}; 